import { StyledProductEngineGearGrid } from '@/components/sections/einstein/product/engine-gear/Grid.styles';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Section } from '@/components/shared/layout/section';
import { Image } from '@/components/shared/media/image';
import { ImageProps } from '@/components/shared/media/image/Image.props';
import { removeHash } from '@/components/shared/utility/format';
import { useInViewRef } from '@/components/shared/utility/hooks';
import { keyframes } from '@/stitches.config';
import { type IMy24ProductMenu } from '@/types/shared';

const AnimationFadeUp = keyframes({
  '0%': {
    opacity: 0,
    transform: 'translateY(30px)',
  },
  '100%': {
    opacity: 1,
    transform: 'translateY(0)',
  },
});

export interface ProductGearProps {
  active: boolean;
  menu: IMy24ProductMenu;
  logoImage: ImageProps;
  bgImage: ImageProps;
  gearImage: ImageProps;
  title: string;
  description: string;
}

export const LCVProductGear = ({
  menu,
  logoImage,
  bgImage,
  gearImage,
  title,
  description,
}: ProductGearProps) => {
  const hash = menu?.hash || '';
  const [viewRef, inView] = useInViewRef({ rootMargin: '0px 0px -1px 0px' });

  return (
    <Section
      ref={viewRef as React.Ref<HTMLDivElement>}
      size="2xl"
      data-hash={hash}
      data-impression="section-gear"
      test={`section_${removeHash(hash)}`}
      backgroundColor="black"
    >
      <Container size={{ '@initial': 'none', '@md': 'md' }}>
        <StyledProductEngineGearGrid alignItems="center" justifyItems="center">
          {!!bgImage && !!gearImage && (
            <Box
              position="relative"
              css={{
                gridRow: 1,
                width: '100%',
                aspectRatio: `${gearImage.width} / ${gearImage.height}`,
                '@md': {
                  gridColumn: '5 / span 4',
                },
                '@lg': {
                  gridColumn: '7 / span 6',
                },
              }}
            >
              <Image
                src={gearImage.src}
                alt={gearImage.alt}
                width={gearImage.width}
                height={gearImage.height}
                aspectRatio="4by3"
                layout="responsive"
                css={{
                  position: 'absolute',
                  zIndex: '$1',
                }}
                objectFit="contain"
                sizes="(max-width: 479px) 400px, 632px"
                useSrcSetPattern
              />
            </Box>
          )}
          <Box
            position="relative"
            zIndex="1"
            css={{
              mt: '-$space-6',
              '> *:not(:first-child)': {
                animationFillMode: 'both',
                animationDuration: '1s',
                animationTimingFunction: 'cubic-bezier(0.33, 1, 0.68, 1)',
                animationName: inView ? `${AnimationFadeUp}` : undefined,
              },
              '> *:nth-child(1)': {
                animationDelay: '0.4s',
              },
              '> *:nth-child(2)': {
                animationDelay: '0.6s',
              },
              '> *:nth-child(3)': {
                animationDelay: '0.8s',
              },

              gridRow: 2,
              px: '$space-6',
              '@sm': {
                px: '$space-12',
              },
              '@md': {
                px: '$space-0',
                gridRow: 1,
                gridColumn: '1 / span 4',
              },
              '@lg': {
                gridColumn: '2 / span 5',
              },
            }}
          >
            {!!logoImage && (
              <Box
                position="relative"
                css={{
                  width: 'clamp(230px, calc(230 / 332 * 100%), 332px)',
                }}
              >
                {!!bgImage && (
                  <Image
                    src={bgImage.src}
                    alt={bgImage.alt}
                    width={bgImage.width}
                    height={bgImage.height}
                    layout="responsive"
                    objectFit="contain"
                    useSrcSetPattern
                    css={{
                      position: 'absolute',
                      top: -22,
                      left: `${100 / (230 / 171)}%`,
                      minWidth: `min(${100 / (230 / 340)}%, 360px)`,
                      '@md': {
                        top: '-55%',
                        left: '-88%',
                        minWidth: '166%',
                      },
                      '@xl': {
                        top: -49.5,
                        left: `-${100 / (332 / 239)}%`,
                        minWidth: `min(${100 / (230 / 497)}%, 497px)`,
                      },
                    }}
                  />
                )}
                <Image
                  src={logoImage.src}
                  alt={logoImage.alt}
                  width={logoImage.width}
                  height={logoImage.height}
                  layout="responsive"
                  css={{
                    mb: '$space-4',
                    '@xl': {
                      mb: '$space-6',
                    },
                  }}
                  objectFit="contain"
                  loadEffect="none"
                  sizes="(max-width: 479px) 300px, 400px"
                  useSrcSetPattern
                />
              </Box>
            )}
            {!!title && (
              <Text
                data-test="title"
                variant="text-white"
                wrap
                size="text-h3"
                line-height="100"
                as="h2"
                dangerouslySetInnerHTML={{ __html: title }}
                css={{
                  textShadow: '0px 0px 4px rgba(255,255,255,0.5)',
                }}
              />
            )}
            {!!description && (
              <Text
                data-test="description"
                variant="text-disabled"
                // size="text-body-2"
                as="p"
                wrap
                dangerouslySetInnerHTML={{ __html: description }}
                css={{
                  marginTop: '$space-2',
                }}
              />
            )}
          </Box>
        </StyledProductEngineGearGrid>
      </Container>
    </Section>
  );
};

LCVProductGear.displayName = 'LCVProductGear';
